import React from 'react'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Box } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Head, LinkRel, MetaTags } from 'src/components/common'
import { wrapper } from 'src/redux'
import { getNltLandingPageBrandData, getNltLandingPageCityData } from 'src/utils/getStaticProps'
import { PAGE_CACHE_CONTROL } from 'server/constants'
import cities from 'src/containers/data/cities.json'
import brands from 'src/containers/data/brands.json'
import {getFormattedMonthYear, replaceDatePlaceholders} from 'src/utils/dateUtils';

const LandingPageHeaderContainer = dynamic(() => import('src/containers/LandingPageHeaderContainer'))
const LandingBrandNltOffersContainer = dynamic(() => import('src/containers/LandingBrandNltOffersContainer'))
const HomeNltOffersContainer = dynamic(() => import('src/containers/HomeNltOffersContainer'))
const InfoBoxContainer2 = dynamic(() => import('src/containers/InfoBoxContainer2'))
const { formattedMonth, formattedYear } = getFormattedMonthYear();

const isSlugCity = (slug: string) => cities.find(city => city.href.toLowerCase() === `/noleggio-a-lungo-termine/${slug.toLowerCase()}`) !== undefined
const isSlugBrand = (slug: string) => brands.find(brand => brand.href === slug.toLowerCase()) !== undefined

interface Props {
  slug: string
  page?: number
  contents: {
    name: string
    incipit: string
    image?: string
    metaTitle?: string
    metaDescription?: string
    text: string | Array<string>
  }
}

const NltLandingPage: NextPage<Props> = ({ slug, contents, page }) => {
  const { t } = useTranslation()
  const router = useRouter()

  const isCity = isSlugCity(slug)
  const isBrand = !isCity

  const formattedIncipit = replaceDatePlaceholders(contents.incipit)
  const formattedMetaDataTitle = replaceDatePlaceholders(contents.metaTitle)
  const formattedMetaDataDescription = replaceDatePlaceholders(contents.metaDescription)


  const pageTitle = isCity
    ? `Noleggio a Lungo Termine a ${contents.name}: Scopri le Migliori Offerte di ${formattedMonth} ${formattedYear}`
    : `Noleggio a Lungo Termine ${contents.name}: Scopri le Migliori Offerte di ${formattedMonth} ${formattedYear}`;

  const crumbs = [
    {
      page: 'Home Page',
      href: '/',
      title: 'Home Page',
    },
    {
      page: 'Offerte Noleggio a Lungo Termine',
      href: '/noleggio-a-lungo-termine',
      title: 'Offerte Noleggio a Lungo Termine',
    },
    {
      page: pageTitle,
      href: router.asPath,
      title: pageTitle,
    },
  ]

  let contentText

  if (Array.isArray(contents.text)) {
    contentText = contents.text.join('')
  } else {
    contentText = contents.text
  }

  return (

    <Box bgColor="baseNeutral.100">
      <Head title={
        formattedMetaDataTitle || `${t('nltPage:header.title')} ${contents.name}`
      } />
      <LinkRel rel="canonical" href={`https://www.carplanner.com/noleggio-a-lungo-termine/${slug}`} />

      <LandingPageHeaderContainer
        title={pageTitle}
        incipit={formattedIncipit}
        crumbs={crumbs}
        image={contents?.image}
        bgColor={isCity ? `baseWhite` : `baseNeutral.100`}
        isCity={isCity}
        action={isCity ? t('nltPage:landingCity.action') : null}
        actionTitle={isCity ? t('nltPage:landingCity.actionTitle', { slug: contents.name }) : null}
        target="offerte-noleggio"
      />

      <Box w="100%" maxW="100%" display="inline-block">
        {isBrand ?
          <>
            <MetaTags
              meta={t(`nltPage:landingBrand.meta`, {
                returnObjects: true,
                brand: contents.name,
                metaDataDescription: formattedMetaDataDescription
              })}
            />
            <LandingBrandNltOffersContainer slug={slug} page={page} />
          </>
          :
          <>
            <MetaTags
              meta={t(`nltPage:landingCity.meta`, {
                returnObjects: true,
                city: contents.name,
                metaDataDescription: formattedMetaDataDescription
              })}
            />
            <Box mb="xxl6" px="l" maxW="8xl" mx="auto">
              <HomeNltOffersContainer
                numPerView={4}
                title={t('nltPage:landingCity.title', { city: contents.name })}
                action={t('indexPage:nlt_offers.action')}
                alt={t('indexPage:nlt_offers.alt')}
                bgColor="baseNeutral.100"
              />

            </Box>
            <InfoBoxContainer2
              title={t('sellPage:cta.title')}
              boldTitle={t('sellPage:cta.boldTitle')}
              description={t('sellPage:cta.description')}
              boldDescription={t('sellPage:cta.boldDescription')}
              action={t('sellPage:cta.action')}
              actionTitle={t('sellPage:cta.actionTitle')}
              href="/offerte-auto"
            />
          </>
        }
      </Box>
      {contentText &&
        <Box bgColor="baseWhite">
          <Box
            maxW={isCity ? `8xl` : null}
            mx="auto"
            color="baseNeutral.700"
            fontFamily="Montserrat-Medium"
            fontSize="md"
            textAlign="left"
            py="xxl"
            px="l"
            className="landing-text"
            dangerouslySetInnerHTML={{ __html: contentText }}
          />
        </Box>
      }
    </Box>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(store => async ({ query, res }) => {
  const { landing } = query
  let contents

  const slug = Array.isArray(landing) ? landing[0] : landing

  if (isSlugBrand(slug)) {
    contents = await getNltLandingPageBrandData(store, slug, query, [{ 'brand': slug }])
    contents.contents.name = (brands.find(brand => brand.href === slug.toLowerCase())).name
  } else {
    contents = await getNltLandingPageCityData(store, slug, query)

    if (!contents.contents.name) {
      return { notFound: true }
    }
  }

  res.setHeader("Cache-Control", PAGE_CACHE_CONTROL);

  return { props: { slug, ...contents } }
})

export default NltLandingPage
